import { useState } from "react";
import { Link } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell
            title="Forgot your password?"
            subtitle="Drop us your email and we'll send you a secure reset link."
            side="virtual"
        >
            {sent ? (
                <div
                    className="rounded-2xl bg-white border border-gold-200/60 shadow-soft p-6 space-y-3"
                    data-testid="forgot-sent-card"
                >
                    <div className="flex items-center gap-3 text-navy-700">
                        <MailCheck className="text-gold-600" />
                        <div className="font-display text-2xl">Check your inbox</div>
                    </div>
                    <p className="text-navy-500 text-sm leading-relaxed">
                        If <span className="text-navy-700 font-medium">{email}</span> matches
                        a Perx account, we&apos;ve sent a password reset link. The link expires
                        in 60 minutes and can be used once.
                    </p>
                    <p className="text-navy-500 text-xs">
                        Didn&apos;t get it? Check spam, then try again in a few minutes.
                    </p>
                    <div className="pt-2">
                        <Link
                            to="/login"
                            data-testid="forgot-back-to-login"
                            className="text-sm text-navy-700 font-semibold hover:text-gold-600"
                        >
                            ← Back to sign in
                        </Link>
                    </div>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                    <div>
                        <Label
                            htmlFor="fp-email"
                            className="text-navy-700 text-xs uppercase tracking-widest"
                        >
                            Registered email
                        </Label>
                        <Input
                            id="fp-email"
                            type="email"
                            required
                            data-testid="forgot-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                        />
                    </div>

                    {err && (
                        <div
                            data-testid="forgot-error"
                            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            {err}
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="forgot-submit"
                        className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                    >
                        {busy ? "Sending reset link…" : "Send reset link"}
                    </Button>

                    <div className="text-sm text-navy-500 text-center">
                        Remembered it?{" "}
                        <Link
                            to="/login"
                            data-testid="forgot-login-link"
                            className="text-navy-700 font-semibold hover:text-gold-600"
                        >
                            Back to sign in
                        </Link>
                    </div>
                </form>
            )}
        </AuthShell>
    );
}
